import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  CreateDatevLaudsEmployeeImportDocument,
  EmployeeHeaderPermissionsDocument,
} from 'generated/resources/graphql'
import type { FeaturesFeature } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { type FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import type { Navigation } from 'modules/core/lib/navigation/context'
import { NavigationPaths } from 'modules/core/lib/navigation/context'
import app from 'modules/core/store/models/app'

import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  canImportEmployees: boolean
  loadingData: boolean
  hasDatevIntegration: boolean
}

function hasCustomTables(featuresFlags: Pick<FeaturesFeature, 'name' | 'enabled'>[]): boolean {
  const customTableFlagEnablers = [
    'employees_custom_tables',
    'trainings_custom_table',
    'competencies_custom_table',
    'growth_plans_custom_table',
  ]
  return customTableFlagEnablers.some((flag) => isFeatureFlagEnabled(featuresFlags, flag))
}

function isFeatureFlagEnabled(
  featuresFlags: Pick<FeaturesFeature, 'name' | 'enabled'>[] | null,
  featureName: string
) {
  return featuresFlags?.find((feature) => feature.name === featureName)?.enabled
}

function addPopupOptions(
  navigation: Navigation<NavigationPaths>,
  featureAvailabilitites: FeatureAvailabilityType[],
  featuresFlags: Pick<FeaturesFeature, 'name' | 'enabled'>[],
  canImportEmployees: boolean,
  canBulkImportCustomTables: boolean,
  canManageTimeoff: boolean,
  canExportEmployees: boolean,
  handleDatevLaudsEmployeesImport: (event: Event) => Promise<void>,
  hasDatevIntegration: boolean
) {
  const options = []

  if (canImportEmployees) {
    if (isFeatureFlagEnabled(featuresFlags, 'legacy_employee_import')) {
      options.push({
        to: navigation.employees.importProcess.fullPath(),
        title: i18n.t('employees.new_importer_title'),
      })
    }

    if (hasDatevIntegration) {
      options.push({
        onClick: handleDatevLaudsEmployeesImport,
        title: i18n.t('employees.importer_selection.options.datev.title'),
        onboardingId: 'employees-import-people-menuitem',
      })
    }

    if (isFeatureFlagEnabled(featuresFlags, 'legacy_employee_edition')) {
      options.push({
        to: navigation.employees.list.import.fullPath(),
        title: i18n.t('employees.import_title'),
      })
    }

    if (!isFeatureFlagEnabled(featuresFlags, 'dev_universal_contracts_edition')) {
      options.push({
        to: navigation.employees.list.importContractsMode.fullPath(),
        title: i18n.t('employees.import_contracts_title'),
      })
    }

    if (canManageTimeoff) {
      options.push({
        title: i18n.t('sidebar.add_button.actions.add_timeoff.title'),
        to: navigation.employees.list.bulkAddTimeOff.fullPath(),
      })
    }

    if (isFeatureAvailabilityEnabled(featureAvailabilitites, Features.timeoff_management)) {
      options.push({
        to: navigation.employees.list.timeoff.fullPath(),
        title: i18n.t('employees.import_timeoff_title'),
      })
    }
  }

  if (canExportEmployees) {
    if (hasDatevIntegration) {
      options.push({
        to: navigation.employees.list.exportTypeSelection.fullPath(),
        title: i18n.t('employees.export_title'),
      })
    } else {
      options.push({
        to: navigation.employees.list.export.fullPath(),
        title: i18n.t('employees.export_title'),
      })
    }
  }

  if (canBulkImportCustomTables && hasCustomTables(featuresFlags)) {
    options.push({
      to: navigation.employees.list.customTablesImport.fullPath(),
      title: i18n.t('employees.import_custom_tables_title'),
    })
  }

  return options
}

export const useEmployeeHeaderPermissions = ({
  canImportEmployees,
  loadingData,
  hasDatevIntegration,
}: Props) => {
  const { navigation } = useNavigation()
  const {
    company: { id: companyId },
    canManageTimeoff,
    access: { id: accessId },
  } = useCurrentSession()

  const { loading: policiesLoading } = useAsyncData(async () => {
    await Promise.all([
      policyAuthorizations.authorizePolicies('employee.create', []),
      policyAuthorizations.authorizePolicies('employee.custom_tables_bulk_import', []),
    ])
  }, [])

  const { data } = useQuery(EmployeeHeaderPermissionsDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: companyId,
      featureNames: [
        'employees_custom_tables',
        'trainings_custom_table',
        'competencies_custom_table',
        'growth_plans_custom_table',
        // go to market
        'legacy_employee_import',
        'legacy_people_edition',
        'legacy_employee_edition',
        'dev_universal_contracts_edition',
      ],
      moduleNames: [AvailableModules.timeoff],
      moduleFeatureNames: [Features.timeoff_management],
    },
  })

  const [createIntegrationsdatevlaudsEmployeeImport, { loading: loadingImport }] = useMutation(
    CreateDatevLaudsEmployeeImportDocument,
    {
      extractorFn: (importData) =>
        importData.integrationsDatevLaudsMutations.createIntegrationsdatevlaudsEmployeeImport,
    }
  )

  const handleDatevLaudsEmployeesImport = async (_event: Event) => {
    const result = await createIntegrationsdatevlaudsEmployeeImport({
      variables: {
        companyId: companyId,
        accessId: accessId,
      },
    })
    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('employees.importer_selection.success_message'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.importer_selection.error_message'),
      })
    }
  }

  if (policiesLoading || !data || loadingData || loadingImport) return

  const canAddEmployee = policyAuthorizations.for('employee.create')
  const canBulkImportCustom = policyAuthorizations.for('employee.custom_tables_bulk_import')
  const canBulkImportCustomTables = policyAuthorizations.for('employee.custom_tables_bulk_import')
  const canExportEmployees = data?.employees?._policies?.exportEmployees?.can ?? false
  const canDownloadImportReport = canExportEmployees || canBulkImportCustom || canImportEmployees

  return {
    action: {
      show: canAddEmployee,
      to: navigation.employees.list.add_employee.fullPath(),
    },
    options: {
      show: canDownloadImportReport,
      options: addPopupOptions(
        navigation,
        data?.companies?.featureAvailabilitiesConnection?.nodes ?? [],
        data?.features?.featuresConnection?.nodes ?? [],
        canImportEmployees,
        canBulkImportCustomTables,
        canManageTimeoff,
        canExportEmployees,
        handleDatevLaudsEmployeesImport,
        hasDatevIntegration
      ),
    },
  }
}
